<script setup lang="ts">
import type { TShapesOpacity } from "~/telestrations/types/shapeOpacityProperty";
import UOpacityRange from "~/common/components/U/Range/OpacityRange.vue";
import { ShapeFillColors } from "~/telestrations/constants/colors";
import { TELESTRATION_TOOLTIP_CONTAINER } from "~/telestrations/constants/tooltip";
import { applyAlphaToHex } from "~/telestrations/helpers/applyAlphaToHex";
import { extractAlphaFromHex } from "~/telestrations/helpers/extractAlphaFromHex";
import { ShapeType } from "~/telestrations/types/canvas";

interface Props {
  type: TShapesOpacity;
}

const props = defineProps<Props>();

const { t } = useI18n();
const shapesStore = useShapesStore();
const isMenuOpened = ref(false);

function onShow() {
  isMenuOpened.value = true;
}

function onHide() {
  isMenuOpened.value = false;
}

// Background color selector logic for text shapes
const backgroundOptions = computed(() => {
  return ShapeFillColors[ShapeType.Ellipse];
});

const selectedBackgroundColor = computed(() => {
  if (shapesStore.selectedShape) {
    return shapesStore.selectedShape[props.type] || "transparent";
  }
  return "transparent";
});

const opacityValue = ref(Math.round(extractAlphaFromHex(shapesStore.selectedShape?.[props.type]) * 100));

const isTransparentSelected = computed(() => selectedBackgroundColor.value === "transparent");

function onOpacityInput(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target) return;

  const inputValue = target.value.trim();
  let val = parseFloat(inputValue);

  if (!Number.isFinite(val)) {
    val = 100;
  }

  val = Math.max(0, Math.min(100, val));
  opacityValue.value = val;

  const color = shapesStore.selectedShape?.[props.type] || "transparent";
  onShapeBackgroundChange(color, val);
}

function onShapeBackgroundChange(value: string, opacity?: number) {
  if (!shapesStore.selectedShape) {
    return;
  }

  if (value === "transparent") {
    shapesStore.updateShape(shapesStore.selectedShape, { [props.type]: value });
    return;
  }

  const color = applyAlphaToHex(value, (opacity ?? opacityValue.value) / 100);
  shapesStore.updateShape(shapesStore.selectedShape, { [props.type]: color });
}
</script>

<template>
  <div class="flex items-center" :class="isMenuOpened ? 'bg-neutral-light-100 dark:bg-neutral-dark-500' : 'hover:bg-neutral-light-100 dark:hover:bg-neutral-dark-500'">
    <USelectMenu
      :model-value="selectedBackgroundColor"
      :options="backgroundOptions"
      input-class="!border-none !gap-x-2 px-1.5"
      list-class="grid grid-cols-4 p-2 gap-x-1 gap-y-2"
      item-class="min-h-max rounded !p-0"
      container-class="p-1"
      variant="inline"
      :divider="false"
      :hide-trailing-icon="true"
      :render-in-portal="false"
      @show="onShow"
      @hide="onHide"
      @update:model-value="onShapeBackgroundChange"
    >
      <template #header>
        <div class="font-red-hat-display p-2 pb-0 dark:bg-neutral-dark-700" @click.stop>
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs font-medium" :class="isTransparentSelected ? 'text-disabled-light dark:text-neutral-dark-400' : 'text-secondary dark:text-neutral-dark-200'">
              {{ t("labels.opacity") }}
            </span>
            <div class="flex h-6 w-14 items-center justify-between rounded border px-2 dark:border-neutral-dark-600 dark:bg-neutral-dark-600" :class="isTransparentSelected ? 'border-disabled-border-light bg-disabled-bg-light' : 'border-gray-200 bg-white'">
              <input
                v-model.number="opacityValue"
                type="number"
                min="0"
                max="100"
                class="text-primary w-5 border-none bg-transparent p-0 text-xs font-bold focus:outline-none focus:ring-0"
                :disabled="isTransparentSelected"
                :class="isTransparentSelected ? 'text-disabled-light dark:text-neutral-dark-400' : 'text-primary'"
                placeholder="100"
                @input="onOpacityInput"
                @keydown.stop
              >
              <span class="ml-1 select-none text-xs font-normal" :class="isTransparentSelected ? 'text-disabled-light dark:text-neutral-dark-400' : 'text-secondary dark:text-neutral-dark-200'">%</span>
            </div>
          </div>
          <UOpacityRange
            :model-value="opacityValue"
            :min="0"
            :max="100"
            :step="1"
            :disabled="isTransparentSelected"
            class="mb-4 mt-2 h-3 w-[152px]"
            :color="selectedBackgroundColor !== 'transparent' ? selectedBackgroundColor : undefined"
            @update:model-value="onOpacityInput"
          />
          <span class="text-xs font-medium text-secondary dark:text-neutral-dark-200">
            {{ t("labels.colors") }}
          </span>
        </div>
      </template>
      <template #option="{ option }">
        <div
          v-if="option.value !== 'transparent'"
          class="rounded-full border-2 p-0.5"
          :style="{ borderColor: selectedBackgroundColor.slice(0, 7) === option.value ? option.value : 'transparent' }"
        >
          <div class="size-6 rounded-full" :style="{ backgroundColor: option.value }" />
        </div>
        <div
          v-else
          class="size-8 rounded-full border-2 p-0.5"
          :class="selectedBackgroundColor === 'transparent' ? 'border-blue-500' : 'border-transparent'"
        >
          <UIcon class="size-6 rounded-full" name="no-shape-fill" />
        </div>
      </template>
      <div
        v-if="selectedBackgroundColor !== 'transparent'"
        v-tooltip="{
          content: t('labels.change_background_color'),
          container: TELESTRATION_TOOLTIP_CONTAINER,
        }"
        class="size-6 rounded-full"
        :style="{ backgroundColor: selectedBackgroundColor }"
      />
      <UIcon
        v-else
        v-tooltip="{
          content: t('labels.change_background_color'),
          container: TELESTRATION_TOOLTIP_CONTAINER,
        }"
        class="size-6 rounded-full"
        name="no-shape-fill"
      />
    </USelectMenu>
  </div>
</template>

<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
